/**
 * Lógica pura de la pantalla "Inteligencia por CUIT": detección del tipo de
 * búsqueda, fusión de subgrafos, escalas visuales, formatos y CSV. Sin React ni
 * fetch, así se testea con `node --test`.
 */
import type { Graph, GraphEdge, GraphNode, PaymentRow } from "../services/cuitIntel";

export type ClientQuery =
  | { tipo: "amount"; min: number; max: number }
  | { tipo: "server"; q: string }
  | { tipo: "empty" };

/** '5.000,50' / '5000.50' / '1,234,567' → número (es-AR y en-US). */
export function parseAmount(raw: string): number | null {
  let s = String(raw ?? "").trim();
  if (!s || !/^[\d.,]+$/.test(s)) return null;
  if (s.includes(",") && s.includes(".")) {
    s = s.lastIndexOf(",") > s.lastIndexOf(".") ? s.replace(/\./g, "").replace(",", ".") : s.replace(/,/g, "");
  } else if (s.includes(",")) {
    s = s.split(",").pop()!.length <= 2 ? s.replace(",", ".") : s.replace(/,/g, "");
  } else if ((s.match(/\./g) ?? []).length > 1 || (s.includes(".") && s.split(".").pop()!.length === 3)) {
    s = s.replace(/\./g, "");
  }
  const v = Number(s);
  return Number.isFinite(v) && v >= 0 ? v : null;
}

/** Monto o rango se resuelven contra /payments; todo lo demás lo clasifica el backend. */
export function classifyClient(raw: string): ClientQuery {
  const q = raw.trim();
  if (!q) return { tipo: "empty" };
  const digits = q.replace(/[\s.\-/]/g, "");
  if (/^\d{11}$/.test(digits)) return { tipo: "server", q };          // CUIT (con o sin guiones)
  const range = q.match(/^\$?\s*([\d.,]+)\s*(?:-|a|hasta)\s*\$?\s*([\d.,]+)$/i);
  if (range) {
    const lo = parseAmount(range[1]), hi = parseAmount(range[2]);
    if (lo != null && hi != null) return { tipo: "amount", min: Math.min(lo, hi), max: Math.max(lo, hi) };
  }
  if (/^\$?\s*[\d.,]+$/.test(q)) {
    const v = parseAmount(q.replace(/^\$\s*/, ""));
    if (v != null) return { tipo: "amount", min: v, max: v };
  }
  return { tipo: "server", q };
}

// ---------- Grafo ----------
export function edgeKey(e: GraphEdge): string {
  return `${e.source}→${e.target}${e.same_cuit ? "=" : ""}`;
}

/** Une un subgrafo nuevo al que ya está en pantalla: nodos por id (gana el que
 *  tiene totales), aristas por par (gana la de más cobros). */
export function mergeGraphs(base: Graph | null, add: Graph): Graph {
  if (!base) return add;
  const nodes = new Map<string, GraphNode>(base.nodes.map((n) => [n.id, n]));
  for (const n of add.nodes) {
    const prev = nodes.get(n.id);
    nodes.set(n.id, prev && prev.n >= n.n ? { ...n, ...prev } : { ...prev, ...n });
  }
  const edges = new Map<string, GraphEdge>(base.edges.map((e) => [edgeKey(e), e]));
  for (const e of add.edges) {
    const k = edgeKey(e);
    const prev = edges.get(k);
    if (!prev || (e.n ?? 0) > (prev.n ?? 0)) edges.set(k, e);
  }
  return {
    ...base,
    nodes: [...nodes.values()],
    edges: [...edges.values()],
    truncated: base.truncated || add.truncated,
  };
}

/** Radio del nodo por monto (raíz cuadrada: el área es proporcional al monto). */
export function nodeRadius(sum: number, maxSum: number, min = 7, max = 26): number {
  if (!maxSum || sum <= 0) return min;
  return min + (max - min) * Math.sqrt(Math.min(sum, maxSum) / maxSum);
}

/** Grosor de la arista por cantidad de cobros (log: 152 cobros no tapan el grafo). */
export function edgeWidth(n: number, maxN: number, min = 1, max = 7): number {
  if (!maxN || n <= 0) return min;
  return min + (max - min) * (Math.log1p(n) / Math.log1p(maxN));
}

export function parseNodeId(id: string): { type: "payer" | "collector"; key: string } {
  const [type, ...rest] = id.split(":");
  return { type: type === "collector" ? "collector" : "payer", key: rest.join(":") };
}

// ---------- Meses ----------
export function monthAdd(yyyymm: string, delta: number): string {
  const y = Number(yyyymm.slice(0, 4)), m = Number(yyyymm.slice(4, 6)) - 1 + delta;
  const d = new Date(Date.UTC(y, m, 1));
  return `${d.getUTCFullYear()}${String(d.getUTCMonth() + 1).padStart(2, "0")}`;
}

export function currentMonthAr(now = new Date()): string {
  const ar = new Date(now.getTime() - 3 * 3600 * 1000);
  return `${ar.getUTCFullYear()}${String(ar.getUTCMonth() + 1).padStart(2, "0")}`;
}

/** Rango de `months` meses terminando en `to` (inclusive). */
export function monthRange(months: number, to = currentMonthAr()): { from: string; to: string } {
  return { from: monthAdd(to, -(months - 1)), to };
}

export function monthsBetween(from: string, to: string): string[] {
  const out: string[] = [];
  for (let m = from; m <= to && out.length < 120; m = monthAdd(m, 1)) out.push(m);
  return out;
}

const MESES = ["ene", "feb", "mar", "abr", "may", "jun", "jul", "ago", "sep", "oct", "nov", "dic"];

export function fmtMonth(yyyymm: string): string {
  if (!/^\d{6}$/.test(yyyymm)) return yyyymm;
  return `${MESES[Number(yyyymm.slice(4, 6)) - 1]} ${yyyymm.slice(0, 4)}`;
}

export function fmtDate8(yyyymmdd: string): string {
  return /^\d{8}$/.test(yyyymmdd) ? `${yyyymmdd.slice(6, 8)}/${yyyymmdd.slice(4, 6)}/${yyyymmdd.slice(0, 4)}` : yyyymmdd;
}

// ---------- Formatos ----------
export function fmtCuit(c?: string | null): string {
  const d = String(c ?? "").replace(/\D/g, "");
  return d.length === 11 ? `${d.slice(0, 2)}-${d.slice(2, 10)}-${d.slice(10)}` : String(c ?? "");
}

const arsFmt = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", maximumFractionDigits: 0 });
const arsFmtCents = new Intl.NumberFormat("es-AR", { style: "currency", currency: "ARS", minimumFractionDigits: 2 });
const intFmt = new Intl.NumberFormat("es-AR");

export function fmtArs(n: number | null | undefined, cents = false): string {
  if (n == null || !Number.isFinite(Number(n))) return "—";
  return (cents ? arsFmtCents : arsFmt).format(Number(n));
}

/** Monto compacto para etiquetas: 1,2 M / 350 k / 5.000. */
export function fmtArsShort(n: number | null | undefined): string {
  const v = Number(n ?? 0);
  if (!Number.isFinite(v)) return "—";
  const a = Math.abs(v);
  if (a >= 1e9) return `$ ${(v / 1e9).toFixed(1).replace(".", ",")} MM`;
  if (a >= 1e6) return `$ ${(v / 1e6).toFixed(1).replace(".", ",")} M`;
  if (a >= 1e4) return `$ ${Math.round(v / 1e3)} k`;
  return `$ ${intFmt.format(Math.round(v))}`;
}

export function fmtInt(n: number | null | undefined): string {
  return n == null ? "—" : intFmt.format(Number(n));
}

export function fmtTsAr(ts?: string | null): string {
  if (!ts) return "—";
  const d = new Date(ts);
  if (isNaN(d.getTime())) return ts;
  return d.toLocaleString("es-AR", { timeZone: "America/Argentina/Buenos_Aires",
    day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit" });
}

export function fmtPct(x: number | null | undefined): string {
  return x == null ? "—" : `${Math.round(Number(x) * 100)} %`;
}

// ---------- CSV (separador ";" + decimal con coma → Excel es-AR) ----------
function csvField(v: unknown): string {
  const s = String(v ?? "").replace(/[\r\n]+/g, " ");
  return /[;"]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
}

export function paymentsCsv(rows: PaymentRow[]): string {
  const head = ["fecha_hora_ar", "monto", "estado", "cuit_pagador", "nombre_pagador", "cbu_pagador", "collector", "id"];
  const lines = [head.join(";")];
  for (const r of rows) {
    lines.push([
      csvField(fmtTsAr(r.ts)), csvField(String(Number(r.amount).toFixed(2)).replace(".", ",")),
      csvField(r.status), csvField(fmtCuit(r.cuit)), csvField(r.name), csvField(r.cbu),
      csvField(r.collector), csvField(r.id),
    ].join(";"));
  }
  return "﻿" + lines.join("\r\n");
}
